import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ArrowLeftIcon, BellAlertIcon, BellSlashIcon, ChatBubbleLeftRightIcon, EyeSlashIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import CommentThread, { StaffBadge } from "./CommentThread";
import cn from "classnames";
import Button from "../common/Button";
import { relativeTime } from "../common/relativeTime";
import { api, byline, CATEGORY_LABELS, FeatureRequestCommentItem, FeatureRequestDetail, FeatureRequestItem, isTermsRequired } from "./api";
import { useFeatureRequests } from "./FeatureRequestsRouter";
import ConfirmModal from "./ConfirmModal";
import NewRequestModal from "./NewRequestModal";
import StaffControls from "./StaffControls";
import StatusChip from "./StatusChip";
import VoteButton from "./VoteButton";
import { useVoteToggle } from "./useVoteToggle";

const RequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isStaff, requestTerms } = useFeatureRequests();
  const [request, setRequest] = useState<FeatureRequestDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [deletingComment, setDeletingComment] = useState<FeatureRequestCommentItem | null>(null);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [togglingSubscription, setTogglingSubscription] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const r = await api.get(id);
      if ("mergedInto" in r) {
        navigate(`/feature-requests/${r.mergedInto}`, { replace: true });
        return;
      }
      setRequest(r);
      // "Unsubscribe" link in the notification emails lands here.
      if (searchParams.get("unsubscribe") === "1") {
        setSearchParams({}, { replace: true });
        if (r.subscribed) {
          api
            .setSubscription(r.id, false)
            .then(() => {
              setRequest((prev) => (prev ? { ...prev, subscribed: false } : prev));
              toast.success("Unsubscribed. You won't get more emails about this request.");
            })
            .catch(() => toast.error("Could not unsubscribe"));
        }
      }
    } catch (e: any) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(e?.response?.data?.error || "Failed to load request");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSubscription = async () => {
    if (!request) return;
    setTogglingSubscription(true);
    try {
      const r = await api.setSubscription(request.id, !request.subscribed);
      setRequest((prev) => (prev ? { ...prev, subscribed: r.subscribed } : prev));
      toast.success(r.subscribed ? "You'll get emails when there's news on this request" : "Unsubscribed from this request");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Could not update your subscription");
    } finally {
      setTogglingSubscription(false);
    }
  };

  const patch = (updated: Partial<FeatureRequestItem>) => setRequest((prev) => (prev ? { ...prev, ...updated } : prev));
  const { toggle, votingId } = useVoteToggle((_id, p) => patch(p));

  // Shared by the bottom composer (top-level) and inline reply composers.
  const submitComment = async (body: string, parentId: string | null): Promise<boolean> => {
    if (!request || !body.trim()) return false;
    try {
      const created = await api.comment(request.id, body.trim(), parentId);
      setRequest((prev) => (prev ? { ...prev, comments: [...prev.comments, created], comment_count: prev.comment_count + 1 } : prev));
      if (parentId) setReplyTo(null);
      return true;
    } catch (e: any) {
      if (isTermsRequired(e)) requestTerms();
      else toast.error(e?.response?.data?.error || "Could not post comment");
      return false;
    }
  };

  const postComment = async () => {
    setPosting(true);
    try {
      if (await submitComment(comment, null)) setComment("");
    } finally {
      setPosting(false);
    }
  };

  const removeComment = async () => {
    if (!request || !deletingComment) return;
    try {
      await api.deleteComment(request.id, deletingComment.id);
      // Reload rather than splice: a deleted parent with replies comes back
      // as a placeholder, and the count is the server's to recompute.
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Could not delete comment");
    } finally {
      setDeletingComment(null);
    }
  };

  const removeRequest = async () => {
    if (!request) return;
    try {
      await api.remove(request.id);
      toast.success("Request deleted");
      navigate("/feature-requests");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Could not delete request");
      setDeleting(false);
    }
  };

  if (notFound) {
    return (
      <div className="flex-1 overflow-auto p-6">
      <div className="max-w-3xl mx-auto">
        <button type="button" onClick={() => navigate("/feature-requests")} className="inline-flex items-center gap-1 text-sm text-base-content/60 hover:text-base-content">
          <ArrowLeftIcon className="size-4" /> All requests
        </button>
        <div className="mt-6 rounded-lg border border-dashed border-base-300 bg-base-100 p-12 text-center">
          <p className="font-medium text-base-content">That request isn't here</p>
          <p className="text-sm text-base-content/60">It may have been removed or merged into another request.</p>
        </div>
      </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto animate-pulse space-y-4">
          <div className="h-5 w-32 rounded bg-base-300" />
          <div className="h-40 rounded-lg bg-base-300" />
          <div className="h-24 rounded-lg bg-base-300" />
        </div>
      </div>
    );
  }

  const canEdit = isStaff || (request.isOwn && request.status === "open");
  // Placeholders for removed parents aren't comments.
  const commentCount = request.comments.filter((c) => !c.removed).length;

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-3xl mx-auto space-y-5">
        <button type="button" onClick={() => navigate("/feature-requests")} className="inline-flex items-center gap-1 text-sm text-base-content/60 hover:text-base-content">
          <ArrowLeftIcon className="size-4" /> All requests
        </button>

        {request.hidden_at && (
          <div className="flex items-center gap-2 rounded-lg border border-base-300 bg-base-200 px-3 py-2 text-xs text-base-content/70">
            <EyeSlashIcon className="size-4" /> Hidden from officers{request.hidden_by ? ` by ${request.hidden_by}` : ""}. Only staff can see this page.
          </div>
        )}

        <article className={cn("p-5 border rounded-lg bg-base-100 border-base-300", request.hidden_at && "border-dashed")}>
          <div className="flex items-start gap-4">
            <VoteButton count={request.vote_count} voted={request.hasVoted} busy={votingId === request.id} onToggle={() => toggle(request)} />
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h1 className="text-xl font-semibold leading-snug">{request.title}</h1>
                <span className="shrink-0 inline-flex items-center gap-1">
                  <button
                    type="button"
                    disabled={togglingSubscription}
                    title={request.subscribed ? "You get emails about this request. Click to unsubscribe." : "Get emails when staff reply or the status changes."}
                    onClick={toggleSubscription}
                    className={cn(
                      "inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors",
                      request.subscribed
                        ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60"
                        : "text-base-content/50 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40"
                    )}
                  >
                    {request.subscribed ? <BellAlertIcon className="size-4" /> : <BellSlashIcon className="size-4" />}
                    {request.subscribed ? "Subscribed" : "Subscribe"}
                  </button>
                {canEdit && (
                  <>
                    <button type="button" className="p-1 rounded-md text-base-content/40 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40" title="Edit" onClick={() => setEditing(true)}>
                      <PencilSquareIcon className="size-4" />
                    </button>
                    <button type="button" className="p-1 rounded-md text-base-content/40 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40" title="Delete" onClick={() => setDeleting(true)}>
                      <TrashIcon className="size-4" />
                    </button>
                  </>
                )}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <StatusChip status={request.status} size="sm" />
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-base-200 text-base-content/60">{CATEGORY_LABELS[request.category]}</span>
                <span className="text-xs text-base-content/40">
                  {byline(request)} · {relativeTime(request.created_at)}
                </span>
              </div>
              <p className="mt-4 text-sm text-base-content/90 leading-6 whitespace-pre-wrap">{request.body}</p>
            </div>
          </div>
        </article>

        {request.staff_response && (
          <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/20 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <StaffBadge />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-300">Official response</span>
              {request.status_changed_at && <span className="text-xs text-base-content/40">· {relativeTime(request.status_changed_at)}</span>}
            </div>
            <p className="text-sm text-base-content/90 leading-6 whitespace-pre-wrap">{request.staff_response}</p>
          </div>
        )}

        {isStaff && <StaffControls request={request} onChanged={(u) => patch(u)} />}

        <section className="space-y-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <ChatBubbleLeftRightIcon className="size-4 text-base-content/50" /> {commentCount === 0 ? "Comments" : `${commentCount} comment${commentCount === 1 ? "" : "s"}`}
          </h2>
          <CommentThread comments={request.comments} isStaff={isStaff} replyTo={replyTo} onReplyTo={setReplyTo} onDelete={setDeletingComment} onSubmitReply={(parentId, body) => submitComment(body, parentId)} />
          <div className="p-4 border rounded-lg bg-base-100 border-base-300">
            <textarea
              className="textarea textarea-bordered bg-base-100 w-full text-sm leading-6 focus:outline-none focus:border-blue-500"
              rows={3}
              placeholder="Add a comment. Remember: no case details."
              value={comment}
              maxLength={4000}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") postComment();
              }}
            />
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="text-[11px] text-base-content/50">Posted under your name and department. ⌘↵ to post.</span>
              <Button variant="primary" size="sm" onClick={postComment} disabled={!comment.trim() || posting}>
                {posting ? "Posting…" : "Post comment"}
              </Button>
            </div>
          </div>
        </section>
      </div>

      <NewRequestModal open={editing} onClose={() => setEditing(false)} request={request} onSaved={(u) => patch(u)} />
      <ConfirmModal
        open={deleting}
        title="Delete this request?"
        body={isStaff && !request.isOwn ? "It will be hidden from officers. Votes and comments are kept." : "Your request and its votes will be removed from the board."}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={removeRequest}
        onClose={() => setDeleting(false)}
      />
      <ConfirmModal open={Boolean(deletingComment)} title="Delete this comment?" body="It will be removed from the thread." confirmLabel="Delete" variant="danger" onConfirm={removeComment} onClose={() => setDeletingComment(null)} />
    </div>
  );
};

export default RequestDetail;
